import type { MapProvider, MapHandle, MapViewOptions, MountContext } from "@genius-tracker/maps";

/**
 * Decide which map provider to use for this build of the web app.
 *
 * Rules:
 *  - `VITE_MAPS_PROVIDER=fallback` always → fallback (safe default
 *    on staging without a key).
 *  - `VITE_MAPS_PROVIDER=google` + a non-empty key → real Google
 *    Maps via `packages/maps/providers/google`.
 *  - `VITE_MAPS_PROVIDER=maplibre` → MapLibre GL with OpenStreetMap
 *    raster tiles (no API key required). `VITE_MAPLIBRE_STYLE_URL`
 *    can point at a MapTiler / Stadia / self-hosted vector style.
 *  - Otherwise → fallback (schematic SVG) with a banner telling
 *    operators the real map isn't configured yet.
 *
 * Product code never branches on this. The page just mounts
 * whatever provider is active.
 *
 * E-BUNDLE-11 — the three provider implementations no longer sit in
 * the entry chunk. `main.tsx` mounts `<MapsSetupProvider>` around the
 * whole app, so a static import here dragged Google, MapLibre AND the
 * fallback (~18 kB minified) into every first paint, even though the
 * landing route is /dashboard and no map exists there. The decision
 * itself only reads `import.meta.env`, so it stays synchronous and
 * `isFallback` / `providerId` keep working exactly as before; only the
 * factory of the CHOSEN provider is pulled in, and only when a
 * `<MapView>` actually mounts. That also means a Google build no
 * longer ships the MapLibre and fallback code at all.
 */
export interface MapsSetup {
  provider: MapProvider;
  isFallback: boolean;
  providerId: "google" | "maplibre" | "fallback";
}

/**
 * Wrap a not-yet-loaded provider in the `MapProvider` shape.
 *
 * Both members of the interface already return Promises, so nothing
 * about the call sites changes: `MapView` calls `provider.mount(...)`
 * and awaits it either way. The module is fetched at most once and
 * only on the first `isReady()`/`mount()` — i.e. when a map surface
 * appears, never at app start.
 */
function deferProvider(id: MapsSetup["providerId"], load: () => Promise<MapProvider>): MapProvider {
  let pending: Promise<MapProvider> | null = null;
  const real = (): Promise<MapProvider> => {
    if (!pending) pending = load();
    return pending;
  };
  return {
    id,
    isReady: (): Promise<void> => real().then((p) => p.isReady()),
    mount: (ctx: MountContext, options: MapViewOptions): Promise<MapHandle> =>
      real().then((p) => p.mount(ctx, options)),
  };
}

/**
 * @param localeOverride GT-I18N-FULL-MATRIX-CLOSEOUT-PART-2-01 (D-12) — the
 *   active app locale, captured at first map mount. The Google Maps JS API
 *   sets its control language via the `language` URL param **read once at
 *   first script load**; passing the user's active locale makes the initial
 *   map match their language instead of always the build-time
 *   `VITE_DEFAULT_LOCALE`. Switching locale at runtime does NOT relabel an
 *   already-loaded Google map without a full reload (a documented Google Maps
 *   constraint — not a bug). Falls back to `VITE_DEFAULT_LOCALE`.
 */
export function resolveMapsSetup(env: ImportMetaEnv, localeOverride?: string): MapsSetup {
  const requested = (env.VITE_MAPS_PROVIDER as string | undefined)?.toLowerCase();
  const apiKey = env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;
  const language =
    localeOverride && localeOverride.length > 0
      ? localeOverride
      : (env.VITE_DEFAULT_LOCALE as string | undefined);
  const mapLibreStyleUrl = env.VITE_MAPLIBRE_STYLE_URL as string | undefined;

  if (requested === "google" && apiKey && apiKey.length > 0) {
    return {
      provider: deferProvider("google", async () => {
        const { createGoogleMapsProvider } = await import("@genius-tracker/maps/providers/google");
        return createGoogleMapsProvider({
          apiKey,
          ...(language ? { language } : {}),
        });
      }),
      isFallback: false,
      providerId: "google",
    };
  }
  if (requested === "maplibre") {
    return {
      provider: deferProvider("maplibre", async () => {
        const { createMapLibreProvider } = await import("@genius-tracker/maps/providers/maplibre");
        return createMapLibreProvider(mapLibreStyleUrl ? { styleUrl: mapLibreStyleUrl } : {});
      }),
      isFallback: false,
      providerId: "maplibre",
    };
  }
  return {
    provider: deferProvider("fallback", async () => {
      const { createFallbackMapProvider } = await import("@genius-tracker/maps/providers/fallback");
      return createFallbackMapProvider();
    }),
    isFallback: true,
    providerId: "fallback",
  };
}
